// ============================================================
//  AuthContext — Authentication state management
// ============================================================
import React, { createContext, useContext, useState } from "react";
import { authAPI } from "../utils/api";

const AuthCtx = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const ROLE_LABELS = {
  ADMIN: "Administrator",
  BENDAHARA: "Bendahara",
  VERIFIKATOR: "Dirjangum",
  PEJABAT: "Wakarumkit",
  EKSEKUTIF: "Karumkit",
  FINANCE: "Finance",
  PERSONEL: "Personel",
};

export const USERS = [
  { id:1, username:"admin",       password:"admin123",    role:"ADMIN",       name:"Administrator",            unit:"IT" },
  { id:2, username:"bendahara",   password:"password123", role:"BENDAHARA",   name:"Bendahara Umum",           unit:"Keuangan" },
  { id:3, username:"pejabat",     password:"password123", role:"PEJABAT",     name:"Wakarumkit (PPK)",         unit:"Keuangan" },
  { id:4, username:"eksekutif",   password:"password123", role:"EKSEKUTIF",   name:"Karumkit",                 unit:"Direksi" },
  { id:5, username:"verifikator", password:"password123", role:"VERIFIKATOR", name:"Dirjangum (Verifikator)",  unit:"Keuangan" },
  { id:6, username:"personel",    password:"personel123", role:"PERSONEL",    name:"Admin Personel RSPAD",     unit:"Personel" },
];

export const VIEWABLE_ROLES = ['ADMIN', 'BENDAHARA', 'VERIFIKATOR', 'PEJABAT', 'EKSEKUTIF', 'FINANCE', 'PERSONEL'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("ifms_user");
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch { return null; }
  });
  const [viewAsRole, setViewAsRole] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(() => {
    try {
      const saved = localStorage.getItem("ifms_user");
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed?.unit_id || null;
    } catch { return null; }
  });

  const login = (u) => {
    if (!u) return;
    localStorage.setItem("ifms_user", JSON.stringify(u));
    setUser(u);
    setViewAsRole(null);
    setSelectedUnitId(u.unit_id || null);
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem("ifms_user");
    localStorage.removeItem("ifms_token");
    setUser(null);
    setViewAsRole(null);
    setSelectedUnitId(null);
  };

  const effectiveRole = viewAsRole || user?.role;
  const effectiveUser = viewAsRole ? { ...user, role: viewAsRole } : user;

  const hasPermission = (...perms) => {
    if (!effectiveUser) return false;
    if (effectiveUser.role === 'ADMIN') return true;
    const userPerms = effectiveUser.permissions || [];
    return perms.every(p => userPerms.includes(p));
  };

  return (
    <AuthCtx.Provider value={{
      user: effectiveUser,
      realUser: user,
      login, logout,
      hasRole: (...r) => effectiveUser && r.includes(effectiveUser.role),
      hasPermission,
      viewAsRole, setViewAsRole,
      isViewingAs: !!viewAsRole,
      selectedUnitId, setSelectedUnitId,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export default AuthCtx;